/**
 * Checkbox field component.
 */

import { useTranslation } from "react-i18next";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import { AlertCircle, HelpCircle } from "lucide-react";
import { cn } from "~/lib/utils";
import type { FieldProps } from "./types";

export function CheckboxField({
  field,
  value,
  touched,
  error,
  label,
  description,
  extendedDescription,
  onChange,
}: FieldProps) {
  const { t } = useTranslation();
  const hasError = touched && !!error;
  const errorId = `${field.name}-error`;
  const descriptionId = `${field.name}-description`;
  const extendedDescriptionId = `${field.name}-extended-description`;

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (description) ariaDescribedByParts.push(descriptionId);
  if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  if (hasError) ariaDescribedByParts.push(errorId);
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  return (
    <div className="space-y-2 animate-in">
      <div className="flex items-start space-x-3">
        <Checkbox
          id={field.name}
          checked={value as boolean}
          onCheckedChange={(checked: boolean | "indeterminate") =>
            onChange(field.name, checked === true)
          }
          aria-invalid={hasError}
          aria-describedby={ariaDescribedBy}
          aria-required={field.validation?.required}
          className={cn(hasError && "border-destructive")}
        />
        <div className="grid gap-1.5 leading-none">
          <Label
            htmlFor={field.name}
            className="text-sm font-medium leading-relaxed cursor-pointer"
          >
            {label}
            {field.validation?.required && (
              <span className="text-destructive ml-1" aria-hidden="true">*</span>
            )}
          </Label>
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {extendedDescription && (
            <details className="text-sm">
              <summary className="text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1 select-none">
                <HelpCircle className="h-3 w-3" aria-hidden="true" />
                {t("common.moreInfo", "More information")}
              </summary>
              <p
                id={extendedDescriptionId}
                className="mt-2 text-muted-foreground pl-4 border-l-2 border-muted"
              >
                {extendedDescription}
              </p>
            </details>
          )}
        </div>
      </div>
      {hasError && error && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-sm text-destructive flex items-center gap-1 ml-7"
        >
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
